<template>
  <div class="panel" v-if="cmSectionState !== 'empty'">
    <div class="cm-sec-head">
      <h2>{{ $t(isFacets ? 'CM_IMAGE_FACETS' : 'CM_MENTIONS') }}</h2>
      <p class="cm-hint">{{ $t(isFacets ? 'CM_IMAGE_FACETS_HINT' : 'CM_MENTIONS_HINT') }}</p>
      <div class="cm-seg">
        <button
          :class="{ active: mentionView === 'chart' }"
          @click="setMentionView('chart')"
        >{{ $t('CM_VIEW_CHART') }}</button>
        <button
          :class="{ active: mentionView === 'table' }"
          @click="setMentionView('table')"
        >{{ $t('CM_VIEW_TABLE') }}</button>
      </div>
    </div>

    <div v-if="loading" class="cm-state">{{ $t('CM_LOADING') }}</div>
    <div v-else-if="error" class="cm-state is-error">{{ error }}</div>

    <div v-if="data && !loading && !error" class="cm-grid-3">
      <div class="cm-mention-section" v-for="group in visibleGroups" :key="group.key">
        <div class="cm-chart-title">{{ $t(group.label) }}</div>
        <div class="cm-mention-row" :class="{ 'is-table': mentionView === 'table' }">
          <div
            v-show="mentionView === 'chart'"
            class="cm-mention-slot"
            :class="{ 'is-hot': hotKey === group.key }"
            @pointerenter="onSlotEnter(group.key, $event)"
            @pointermove="onSlotMove(group.key, $event)"
            @pointerleave="onSlotLeave($event)"
            @pointerdown="onSlotDown(group.key, $event)"
          >
            <div
              v-if="hotKey === group.key && washMask"
              class="cm-chart-wash"
              :style="washMask"
            ></div>
            <div
              v-if="tip && tip.key === group.key"
              class="cm-mention-tooltip"
              v-html="tip.html"
            ></div>
            <div :ref="group.key" class="cm-chart-sm"></div>
          </div>

        <table class="cm-table cm-mention-table">
          <thead>
            <tr>
              <th
                :class="classTableHead(group.key, 'term')"
                @click="toggleSort(group.key, 'term')"
              >{{ $t(group.colLabel) }}</th>
              <th
                :class="['is-num', classTableHead(group.key, 'share')]"
                @click="toggleSort(group.key, 'share')"
              >{{ $t('CM_COL_SHARE') }}</th>
              <th
                :class="['is-num', classTableHead(group.key, 'er')]"
                @click="toggleSort(group.key, 'er')"
              >{{ $t('CM_COL_REL_ER') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in sortedTops[group.key]"
              :key="item.term"
              @pointerenter="onRowEnter(group.key, item, $event)"
              @pointerleave="onRowLeave($event)"
              @click="onRowClick(group.key, item, $event)"
            >
              <td
                class="cm-mention-term"
                :data-cm-idx="chartIndexOf(group.key, item.term)"
              >
                <span
                  class="cm-dot"
                  :style="{ background: colorOf(chartIndexOf(group.key, item.term)) }"
                ></span>
                <span
                  class="cm-term"
                  :class="{ 'is-on': hotKey === group.key && hotTerm === item.term }"
                  :title="item.term"
                  @click.stop="onTermClick(group.key, item, $event)"
                ><span class="cm-mention-name">{{ item.term }}</span></span>
              </td>
              <td class="is-num">
                <span v-if="mentionView === 'table'" class="cm-bar">
                  <span class="cm-bar-track">
                    <span class="cm-bar-fill" :style="{ width: item.share_pct + '%' }"></span>
                  </span>
                  <span class="cm-bar-val">{{ item.share_pct }}%</span>
                </span>
                <span v-else class="cm-mention-num">{{ item.share_pct }}%</span>
              </td>
              <td class="is-num">
                <span v-if="mentionView === 'table'" class="cm-bar">
                  <span class="cm-bar-track">
                    <span
                      class="cm-bar-fill is-alt"
                      :style="{ width: barWidth(item.avg_relative_er, group.key) }"
                    ></span>
                  </span>
                  <span class="cm-bar-val">{{ formatPct(item.avg_relative_er) }}</span>
                </span>
                <span
                  v-else
                  class="cm-rel"
                  :class="item.avg_relative_er == null ? null : 'is-up'"
                  :title="item.avg_relative_er == null ? $t('CM_MENTIONS_THIN', { n: minPostsForAvg }) : null"
                >{{ formatPct(item.avg_relative_er) }}</span>
              </td>
            </tr>
          </tbody>
        </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from "echarts";
import { debounce } from "lodash";
import cmScopeMixin from "@/mixins/cmScopeMixin";
import cmSectionMixin from "@/mixins/cmSectionMixin";
import cmTableSortMixin, { createSortState } from "@/mixins/cmTableSortMixin";
import cmApi from "@/api/controllers/content-monitoring";
import bus from "@/models/content-monitoring/bus";
import { enableTouchTooltip } from "@/models/charts/echarts-touch";
import { imageFacetLabel, isTranslatableFacet } from "@/models/content-monitoring/image-labels";

/**
 * Группы блока — упоминания, одинаковые на всех вкладках: аккаунты, домены и бренды
 * (бренды, известные люди и персонажи из текста и с картинок; у картинок аккаунты и
 * домены — из надписей на них). Ссылки сведены к доменам: у каждой ссылки один-два
 * поста, и топ ссылок ничего не говорит. Классификацию картинки (категория/тип/бренд)
 * сервер отдаёт только по groups=image_facets — блок умеет нарисовать и её: что
 * прислали, говорит types.
 */
const GROUPS = {
  account: { key: "accounts", label: "CM_MENTIONS_ACCOUNTS", colLabel: "CM_COL_ACCOUNT" },
  domain: { key: "domains", label: "CM_MENTIONS_DOMAINS", colLabel: "CM_COL_DOMAIN" },
  link: { key: "links", label: "CM_MENTIONS_LINKS", colLabel: "CM_COL_LINK" },
  category: { key: "categories", label: "CM_IMG_CATEGORIES", colLabel: "CM_COL_CATEGORY" },
  content_type: { key: "content_types", label: "CM_IMG_CONTENT_TYPES", colLabel: "CM_COL_CONTENT_TYPE" },
  // бренды есть на обеих вкладках: среди упоминаний — «Упомянутые бренды»,
  // среди осей картинки — просто «Бренды» (см. groups())
  brand: { key: "brands", label: "CM_MENTIONS_BRANDS", facetLabel: "CM_IMG_BRANDS", colLabel: "CM_COL_BRAND" },
};

const DEFAULT_TYPES = ["account", "domain", "brand"];
// Аккаунт и домен — упоминания, а не слова: drill-down ищет их по типу (entity),
// иначе @apple смешался бы со словом «apple». Бренд — общая ось со словами.
const ENTITY_TYPES = ["account", "domain", "link"];
const SORT_FIELDS = ["term", "share", "er"];

export default {
  name: "Cm-mentions",
  mixins: [cmScopeMixin, cmSectionMixin, cmTableSortMixin],

  data() {
    return {
      data: null,
      loading: false,
      error: "",
      charts: [],
      chartRefs: {},
      mentionView: "chart",
      focus: null,
      pinned: false,
      tip: null,
      hotKey: null,
      hotTerm: null,
      washMask: null,
      // состояние сортировки заводим на все группы сразу: набор зависит от
      // вкладки, а создавать его на лету — значит терять выбранную сортировку
      sorts: Object.keys(GROUPS).reduce((acc, type) => {
        acc[GROUPS[type].key] = createSortState(SORT_FIELDS, "term");
        return acc;
      }, {}),
    };
  },

  computed: {
    /** Порог сервера: ниже него средняя активность не показывается. */
    minPostsForAvg() {
      return (this.data && this.data.min_posts_for_avg) || 0;
    },

    /** Какие группы прислал сервер (по ним же и заголовок блока). */
    groups() {
      const types = (this.data && this.data.types) || DEFAULT_TYPES;
      const facets = this.isFacets;
      return types
        .filter((type) => GROUPS[type])
        .map((type) => {
          const g = GROUPS[type];
          return { type, ...g, label: facets && g.facetLabel ? g.facetLabel : g.label };
        });
    },

    /** Пришли оси картинки, а не упоминания: бренд есть и там, и там, поэтому
     * судим по категориям / форме подачи. */
    isFacets() {
      const types = (this.data && this.data.types) || DEFAULT_TYPES;
      return types.some((type) => type === "category" || type === "content_type");
    },
    /** Только группы, у которых реально есть элементы — пустые кольца не рисуем. */
    visibleGroups() {
      if (!this.data) return [];
      return this.groups.filter((g) => {
        const group = this.data[g.key];
        return group && group.items && group.items.length > 0;
      });
    },

    /** Есть ли что показывать: хотя бы одна группа с элементами. */
    hasData() {
      return this.visibleGroups.length > 0;
    },

    /**
     * Топы по группам, посчитанные один раз на загрузку.
     * Порядок фиксирован для pie (цвета/сегменты); таблица сортируется отдельно.
     */
    tops() {
      return this.visibleGroups.reduce((acc, group) => {
        acc[group.key] = this.buildTop(group.key);
        return acc;
      }, {});
    },

    /** Отсортированные строки легенды — pie остаётся в исходном порядке tops. */
    sortedTops() {
      const opts = {
        fieldMap: {
          term: "term",
          share: "share_pct",
          er: "avg_relative_er",
        },
        stringFields: ["term"],
      };
      return this.visibleGroups.reduce((acc, group) => {
        acc[group.key] = this.sortRows(this.tops[group.key] || [], group.key, opts);
        return acc;
      }, {});
    },

    palette() {
      const c = this.$colors;
      return [c.blue, c.viol, c.red, c.green, c.orange, c.pink, c.yellow];
    },
  },

  methods: {
    colorOf(i) {
      return this.palette[i % this.palette.length];
    },

    /** Индекс в pie/tops — цвета и highlight не съезжают при сортировке таблицы. */
    chartIndexOf(key, term) {
      const rows = this.tops[key] || [];
      return rows.findIndex((row) => row.term === term);
    },

    /** null = упоминаний мало, значение не показательно (не ноль). */
    formatPct(value) {
      return value == null ? "—" : `${value}%`;
    },

    /**
     * Подпись строки. У категорий и типов картинки в базе лежит латинский слаг
     * (ключ обязан быть стабильным) — на экране показываем перевод.
     */
    openTerm(item) {
      if (!item || !item.drillTerm) return;
      bus.$emit("cm:term", item.drillTerm, this.isFacets ? "media" : "", { entity: item.isEntity });
    },

    labelOf(group, item) {
      if (!isTranslatableFacet(group.type)) return item.term;
      return imageFacetLabel(group.type, item.entity || item.term, (key) => this.$t(key));
    },

    /** Топ-6 + «Другие» — иначе кольцо нечитаемо. */
    buildTop(key) {
      const group = this.data && this.data[key];
      if (!group) return [];

      const meta = this.groups.find((g) => g.key === key) || {};
      const rows = group.items.map((item) => ({
        ...item,
        drillTerm: item.entity || item.term,
        isEntity: ENTITY_TYPES.indexOf(meta.type) !== -1,
        term: this.labelOf(meta, item),
      }));
      const head = rows.slice(0, 6);
      const tail = rows.slice(6);
      if (!tail.length) return head;

      const restMentions = tail.reduce((acc, r) => acc + r.mentions, 0);
      const restShare = tail.reduce((acc, r) => acc + r.share_pct, 0);
      // строки без активности (мало упоминаний) в среднее не берём — иначе они
      // тянули бы «Другие» к нулю; если таких строк весь хвост, значения нет
      const withEr = tail.filter((r) => r.avg_relative_er != null);
      const avgEr = withEr.length
        ? withEr.reduce((acc, r) => acc + r.avg_relative_er, 0) / withEr.length
        : null;

      return [
        ...head,
        {
          term: this.$t("CM_MENTIONS_OTHER"),
          mentions: restMentions,
          share_pct: Math.round(restShare * 10) / 10,
          avg_relative_er: avgEr == null ? null : Math.round(avgEr * 10) / 10,
        },
      ];
    },

    itemIndexFromParams(params) {
      // series 0 = share pie → dataIndex; series 1..n = ER gauge rings → seriesIndex-1
      if (params.seriesIndex === 0) return params.dataIndex;
      return params.seriesIndex - 1;
    },

    /** Тултип как везде: прозрачный фон + разметка .tooltip/.tooltip-list. */
    tooltipHtml(key, item) {
      const idx = this.chartIndexOf(key, item.term);
      const color = this.colorOf(idx < 0 ? 0 : idx);
      const postsName = this.$t("CM_COL_POSTS");
      const shareName = this.$t("CM_COL_SHARE");
      const erName = this.$t("CM_COL_REL_ER");

      return `<div class="tooltip">
        <span class="date" style="color:${color};opacity:1">${item.term}</span>
        <ul class="tooltip-list">
          <li data-value="${item.mentions || 0}">
            <div class="soc-sea"><span class="cm-dot" style="background:${color};margin-right:6px"></span>${postsName}</div>${item.mentions || 0}
          </li>
          <li data-value="${item.share_pct}">
            <div class="soc-sea"><span class="cm-dot" style="background:${color};margin-right:6px"></span>${shareName}</div>${item.share_pct}%
          </li>
          <li data-value="${item.avg_relative_er == null ? 0 : item.avg_relative_er}">
            <div class="soc-sea"><span class="cm-dot" style="background:${color};margin-right:6px"></span>${erName}</div>${this.formatPct(item.avg_relative_er)}
          </li>
        </ul>
      </div>`;
    },

    /**
     * Внешнее кольцо — доли (сегменты, как сейчас).
     * Внутренние — отн. ER: отдельная series на каждый пункт (как PostsStats radarMany).
     */
    buildSeries(items, span) {
      const shareName = this.$t("CM_COL_SHARE");
      const erName = this.$t("CM_COL_REL_ER");
      const maxEr = Math.max(...items.map((item) => Number(item.avg_relative_er) || 0), 1) * 1.3;

      const shareSeries = {
        id: "share",
        name: shareName,
        type: "pie",
        radius: this.shareRadius(span),
        center: ["50%", "50%"],
        startAngle: 90,
        padAngle: 2,
        avoidLabelOverlap: true,
        label: { show: false },
        labelLine: { show: false },
        emphasis: { scale: false },
        data: items.map((item, i) => ({
          name: item.term,
          value: item.mentions,
          itemStyle: {
            color: this.colorOf(i),
            borderRadius: 10,
            borderColor: "#fff",
            borderWidth: 2,
          },
        })),
      };

      // PostsStats-style gauge rings: value + transparent remainder per item
      const erSeries = items.map((item, idx) => {
        const value = Number(item.avg_relative_er) || 0;
        const color = this.colorOf(idx);

        return {
          name: `${erName}: ${item.term}`,
          type: "pie",
          animation: false,
          clockwise: true,
          radius: this.erRadius(idx, span),
          center: ["50%", "50%"],
          startAngle: 90,
          label: { show: false },
          labelLine: { show: false },
          data: [
            {
              value,
              name: item.term,
              itemStyle: {
                color,
                borderRadius: 10,
              },
              emphasis: {
                scale: false,
              },
            },
            {
              value: Math.max(maxEr - value, 0.01),
              name: "empty",
              itemStyle: { color: "transparent" },
              tooltip: { show: false },
              label: { show: false },
              labelLine: { show: false },
              emphasis: {
                disabled: true,
                itemStyle: { color: "transparent" },
              },
            },
          ],
        };
      });

      return [shareSeries, ...erSeries];
    },

    /** Радиус в пикселях от размера слота: холст больше слота, проценты его раздули бы. */
    slotSpan(key) {
      const chart = this.chartRefs[key];
      const slot = chart && chart.getDom() && chart.getDom().parentNode;
      if (!slot) return 168;
      return Math.min(slot.clientWidth || 230, slot.clientHeight || 168);
    },

    shareRadius(span, grown) {
      const outer = span * 0.45 * (grown ? 1.5 : 1);
      return [Math.max(outer - 6, 4), outer];
    },

    erRadius(idx, span, grown) {
      const shareOuter = span * 0.45 * (grown ? 1.5 : 1);
      const outer = Math.max(shareOuter * ((70 - idx * 7) / 90), 10);
      return [Math.max(outer - 5, 4), outer];
    },

    /** То же кольцо, только больше, с раздвинутыми сегментами и подписями. */
    grownSeries(key) {
      const span = this.slotSpan(key);
      const items = this.tops[key] || [];
      const series = this.buildSeries(items, span);
      const labelStyle = {
        show: true,
        position: "outside",
        alignTo: "labelLine",
        width: 210,
        overflow: "truncate",
        lineHeight: 14,
        fontSize: 12,
        fontWeight: 400,
        color: "#182228",
        formatter: (params) => {
          const item = items[params.dataIndex];
          const full = params.name || "";
          const name = full.length > 20 ? `${full.slice(0, 20)}…` : full;
          return item ? `${name} (${item.share_pct}%)` : name;
        },
      };
      series[0].radius = this.shareRadius(span, true);
      series[0].padAngle = 5;
      series[0].animationDurationUpdate = 200;
      series[0].animationEasingUpdate = "cubicOut";
      series[0].label = labelStyle;
      series[0].labelLine = {
        show: true,
        length: 8,
        length2: 6,
        lineStyle: { width: 1, opacity: 0.35 },
      };
      series[0].emphasis = { scale: false, label: labelStyle };
      series[0].labelLayout = (params) => this.clampLabel(key, params);
      items.forEach((item, idx) => {
        const ring = series[idx + 1];
        if (!ring) return;
        ring.radius = this.erRadius(idx, span, true);
        ring.animation = false;
      });
      return series;
    },

    /** Подпись не уезжает за край окна. x/y — якорь текста, не левый верх рамки. */
    clampLabel(key, params) {
      const chart = this.chartRefs[key];
      const rect = params && params.labelRect;
      if (!chart || !rect || !rect.width || !rect.height) return {};
      if (!this._clampBox || this._clampBox.key !== key) {
        this._clampBox = { key, rect: chart.getDom().getBoundingClientRect() };
      }
      const host = this._clampBox.rect;
      const pad = 8;
      const viewRight = document.documentElement.clientWidth - pad;
      const viewBottom = window.innerHeight - pad;
      let dx = 0;
      let dy = 0;
      const left = host.left + rect.x;
      const right = left + rect.width;
      const top = host.top + rect.y;
      const bottom = top + rect.height;
      if (left < pad) dx = pad - left;
      else if (right > viewRight) dx = viewRight - right;
      if (top < pad) dy = pad - top;
      else if (bottom > viewBottom) dy = viewBottom - bottom;
      if (!dx && !dy) return {};
      let x = rect.x + dx;
      let y = rect.y + dy;
      if (params.align === "right") x += rect.width;
      else if (params.align === "center") x += rect.width / 2;
      if (params.verticalAlign === "bottom") y += rect.height;
      else if (params.verticalAlign === "middle") y += rect.height / 2;
      return { x, y };
    },
    itemAtPointer(key, event) {
      const slot = event.currentTarget;
      const rect = slot.getBoundingClientRect();
      const dx = event.clientX - (rect.left + rect.width / 2);
      const dy = event.clientY - (rect.top + rect.height / 2);
      let ang = Math.atan2(dx, -dy);
      if (ang < 0) ang += Math.PI * 2;
      const items = this.tops[key] || [];
      const total = items.reduce((sum, item) => sum + (Number(item.mentions) || 0), 0) || 1;
      let cursor = 0;
      for (let i = 0; i < items.length; i += 1) {
        const sweep = ((Number(items[i].mentions) || 0) / total) * Math.PI * 2;
        if (ang < cursor + sweep || i === items.length - 1) return items[i];
        cursor += sweep;
      }
      return null;
    },

    showHot(key, item) {
      if (this.focus) this.clearFocus();
      if (this.hotKey !== key) {
        this.clearHot();
        this.hotKey = key;
        this._clampBox = null;
        this.$nextTick(() => {
          if (this.hotKey !== key) return;
          const chart = this.chartRefs[key];
          if (!chart || chart.isDisposed()) return;
          chart.resize();
          const outer = this.shareRadius(this.slotSpan(key), true)[1];
          const cx = chart.getWidth() / 2;
          const cy = chart.getHeight() / 2;
          chart.setOption({
            backgroundColor: "transparent",
            animation: true,
            animationDurationUpdate: 200,
            animationEasingUpdate: "cubicOut",
            series: this.grownSeries(key),
            graphic: [{
              id: "disc",
              type: "circle",
              silent: true,
              z: -10,
              transition: ["shape"],
              enterFrom: {
                shape: { cx, cy, r: outer / 1.5 + 2 },
              },
              shape: { cx, cy, r: outer + 2 },
              style: { fill: "#fff" },
            }],
          }, { replaceMerge: ["series"] });
          this.placeWashes(chart, key);
        });
      }
      this.hotTerm = item ? item.term : null;
    },

    clearHot() {
      const key = this.hotKey;
      if (!key) return;
      clearTimeout(this._washTimer);
      this._washGen = (this._washGen || 0) + 1;
      this.hotKey = null;
      this.hotTerm = null;
      this.washMask = null;
      this._clampBox = null;
      if (this.tip && this.tip.key === key && !this.focus) this.tip = null;
      this.$nextTick(() => {
        const chart = this.chartRefs[key];
        if (!chart || chart.isDisposed()) return;
        chart.resize();
        this.paintResting(key);
      });
    },

    /** Маленькое кольцо целиком, без хвостов от увеличенного. */
    paintResting(key) {
      const chart = this.chartRefs[key];
      if (!chart || chart.isDisposed()) return;
      chart.setOption({
        backgroundColor: "transparent",
        animation: false,
        legend: { show: false },
        tooltip: { show: false },
        series: this.buildSeries(this.tops[key] || [], this.slotSpan(key)),
        graphic: [],
      }, { notMerge: true });
    },

    /** Одно неровное гало: шире там, где подписи, и всё равно держит их внутри. */
    placeWashes(chart, key) {
      if (this.hotKey !== key || !chart || chart.isDisposed()) return;
      const w = chart.getWidth();
      const h = chart.getHeight();
      if (!w || !h) return;
      const outer = this.shareRadius(this.slotSpan(key), true)[1];
      const cx = w / 2;
      const cy = h / 2;
      const items = this.tops[key] || [];
      const total = items.reduce((sum, item) => sum + (Number(item.mentions) || 0), 0) || 1;
      const boxes = [];
      let cursor = 0;
      items.forEach((item) => {
        const sweep = ((Number(item.mentions) || 0) / total) * Math.PI * 2;
        const mid = cursor + sweep / 2;
        cursor += sweep;
        const chars = Math.min((item.term || "").length, 20) + 8;
        const tw = Math.min(210, Math.max(72, chars * 7));
        const th = 22;
        const dist = outer + 26;
        const ax = Math.sin(mid) * dist;
        const ay = -Math.cos(mid) * dist;
        const x0 = (Math.sin(mid) >= 0 ? ax : ax - tw) - 16;
        const y0 = ay - th / 2 - 12;
        boxes.push({ x0, y0, x1: x0 + tw + 32, y1: y0 + th + 24 });
      });
      const samples = 24;
      const maxR = Math.min(w, h) / 2 - 6;
      const radii = [];
      for (let i = 0; i < samples; i += 1) {
        const ang = (i / samples) * Math.PI * 2;
        let r = outer + 46 + ((i * 5) % 4) * 5;
        boxes.forEach((box) => {
          const hit = this.rayFar(ang, box);
          if (hit + 10 > r) r = hit + 10;
        });
        radii.push(Math.min(r, maxR));
      }
      const pts = radii.map((r, i) => {
        const ang = (i / samples) * Math.PI * 2;
        return [cx + Math.sin(ang) * r, cy - Math.cos(ang) * r];
      });
      this.washMask = this.buildWashMask(w, h, this.blobPath(pts));
    },

    /** Дальняя точка луча из центра, если он пересекает прямоугольник подписи. */
    rayFar(ang, box) {
      const dx = Math.sin(ang);
      const dy = -Math.cos(ang);
      let t0 = 0;
      let t1 = Infinity;
      const slabs = [[dx, box.x0, box.x1], [dy, box.y0, box.y1]];
      for (let i = 0; i < slabs.length; i += 1) {
        const d = slabs[i][0];
        const min = slabs[i][1];
        const max = slabs[i][2];
        if (Math.abs(d) < 1e-6) {
          if (min > 0 || max < 0) return 0;
          continue;
        }
        let a = min / d;
        let b = max / d;
        if (a > b) {
          const swap = a;
          a = b;
          b = swap;
        }
        if (a > t0) t0 = a;
        if (b < t1) t1 = b;
        if (t0 > t1) return 0;
      }
      return t1 > 0 ? t1 : 0;
    },

    /** Замкнутая гладкая кривая через точки контура. */
    blobPath(pts) {
      const n = (value) => Math.round(value * 10) / 10;
      const len = pts.length;
      let d = `M ${n(pts[0][0])} ${n(pts[0][1])}`;
      for (let i = 0; i < len; i += 1) {
        const p0 = pts[(i - 1 + len) % len];
        const p1 = pts[i];
        const p2 = pts[(i + 1) % len];
        const p3 = pts[(i + 2) % len];
        const c1x = p1[0] + (p2[0] - p0[0]) / 6;
        const c1y = p1[1] + (p2[1] - p0[1]) / 6;
        const c2x = p2[0] - (p3[0] - p1[0]) / 6;
        const c2y = p2[1] - (p3[1] - p1[1]) / 6;
        d += ` C ${n(c1x)} ${n(c1y)} ${n(c2x)} ${n(c2y)} ${n(p2[0])} ${n(p2[1])}`;
      }
      return `${d} Z`;
    },

    buildWashMask(w, h, path) {
      const n = (value) => Math.round(value * 10) / 10;
      const shape = `<path d="${path}" fill="white"/>`;
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${n(w)}" height="${n(h)}" viewBox="0 0 ${n(w)} ${n(h)}"><defs><filter id="s" x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="16"/></filter></defs><g>${shape}</g><g filter="url(#s)">${shape}</g></svg>`;
      const url = `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
      return {
        WebkitMaskImage: url,
        maskImage: url,
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskSize: "100% 100%",
        maskSize: "100% 100%",
        WebkitMaskPosition: "center",
        maskPosition: "center",
      };
    },

    onSlotEnter(key, event) {
      if (event.pointerType === "touch") return;
      this.cancelFocus();
      this.cancelLeave();
      this.pinned = false;
      this.showHot(key, this.itemAtPointer(key, event));
    },

    onSlotMove(key, event) {
      if (event.pointerType === "touch" || this.hotKey !== key) return;
      const item = this.itemAtPointer(key, event);
      const term = item && item.term;
      if (term === this.hotTerm) return;
      this.hotTerm = term;
    },

    onSlotLeave(event) {
      if (event.pointerType === "touch") return;
      const slot = event.currentTarget;
      const next = event.relatedTarget;
      if (slot && next && next.nodeType === 1 && slot.contains(next)) return;
      this.pinned = false;
      this.clearHot();
    },

    onSlotDown(key, event) {
      if (event.pointerType !== "touch") return;
      this.pinned = true;
      this.showHot(key, this.itemAtPointer(key, event));
    },

    renderCharts() {
      if (!this.data || !this.hasData) return;

      // на resize всегда пересоздаём: setOption+resize() оставляет старый
      // кэш размера canvas у pie-колец
      this.disposeCharts();

      this.charts = this.visibleGroups
        .map((group) => {
          const holder = this.$refs[group.key];
          const el = Array.isArray(holder) ? holder[0] : holder;
          if (!el) return null;

          const chart = echarts.init(el, null, {
            renderer: "canvas",
            useDirtyRect: false,
            devicePixelRatio: 1,
          });
          enableTouchTooltip(chart);
          this.chartRefs[group.key] = chart;
          return chart;
        })
        .filter(Boolean);

      this.visibleGroups.forEach((group) => {
        const chart = this.chartRefs[group.key];
        if (!chart) return;
        const items = this.tops[group.key] || [];
        const span = this.slotSpan(group.key);

        chart.setOption(
          {
            backgroundColor: "transparent",
            legend: { show: false },
            tooltip: { show: false },
            series: this.buildSeries(items, span),
          },
          { notMerge: true },
        );
        chart.resize();
      });
    },

    setMentionView(view) {
      if (this.mentionView === view) return;
      this.clearFocus();
      this.mentionView = view;
      if (view === "chart") this.$nextTick(this.resizeCharts);
    },

    maxErOf(key) {
      const rows = this.tops[key] || [];
      return Math.max(...rows.map((row) => Number(row.avg_relative_er) || 0), 1);
    },

    barWidth(value, key) {
      if (value == null) return "0%";
      return `${Math.max(2, Math.round((Number(value) / this.maxErOf(key)) * 100))}%`;
    },

    pointerInHotSlot(event) {
      if (!this.hotKey) return false;
      const chart = this.chartRefs[this.hotKey];
      const slot = chart && chart.getDom() && chart.getDom().parentNode;
      if (!slot) return false;
      const rect = slot.getBoundingClientRect();
      return event.clientX >= rect.left
        && event.clientX <= rect.right
        && event.clientY >= rect.top
        && event.clientY <= rect.bottom;
    },
    onRowEnter(key, item, event) {
      if (event.pointerType === "touch") return;
      if (this.pointerInHotSlot(event)) return;
      this.cancelLeave();
      this.queueFocus(key, item);
    },

    onRowLeave(event) {
      if (event.pointerType === "touch" || this.pinned) return;
      this.cancelFocus();
      this.cancelLeave();
      this._leaveTimer = setTimeout(() => {
        this._leaveTimer = null;
        this.clearFocus();
      }, 280);
    },

    cancelLeave() {
      if (!this._leaveTimer) return;
      clearTimeout(this._leaveTimer);
      this._leaveTimer = null;
    },

    /** Быстрый проход курсором не перерисовывает кольцо. */
    queueFocus(key, item) {
      this.cancelFocus();
      this._focusTimer = setTimeout(() => {
        this._focusTimer = null;
        this.applyFocus(key, item);
      }, 200);
    },

    cancelFocus() {
      if (!this._focusTimer) return;
      clearTimeout(this._focusTimer);
      this._focusTimer = null;
    },

    onTermClick(key, item, event) {
      if (event.pointerType === "touch") {
        const same = this.pinned
          && this.focus
          && this.focus.key === key
          && this.focus.item.term === item.term;
        this.pinned = true;
        this.applyFocus(key, item);
        if (!same) return;
      }
      this.openTerm(item);
    },

    onRowClick(key, item, event) {
      if (event.pointerType !== "touch") return;
      this.pinned = true;
      this.applyFocus(key, item);
    },

    onDocPointer(event) {
      if (this.hotKey) {
        const chart = this.chartRefs[this.hotKey];
        const slot = chart && chart.getDom() && chart.getDom().parentNode;
        if (!slot || !slot.contains(event.target)) this.clearHot();
      }
      if (!this.pinned || !this.$el || this.$el.contains(event.target)) return;
      this.clearFocus();
    },

    /** Доля терма от 100% и его отн. активность — вместо общего кольца. */
    focusSeries(key, item) {
      const idx = this.chartIndexOf(key, item.term);
      const color = this.colorOf(idx < 0 ? 0 : idx);
      const share = Number(item.share_pct) || 0;
      const er = Number(item.avg_relative_er) || 0;
      const maxEr = Math.max(this.maxErOf(key), er, 1);
      return [
        {
          id: "share",
          type: "pie",
          radius: this.shareRadius(this.slotSpan(key)),
          center: ["50%", "50%"],
          startAngle: 90,
          padAngle: 3,
          label: {
            show: true,
            position: "center",
            fontSize: 13,
            fontWeight: 600,
            color: "#182228",
            formatter: () => `${share}%`,
          },
          labelLine: { show: false },
          emphasis: { scale: false },
          data: [
            {
              name: item.term,
              value: Math.max(share, 0.01),
              itemStyle: { color, borderRadius: 8 },
            },
            {
              name: "empty",
              value: Math.max(100 - share, 0.01),
              itemStyle: { color: "#eef1f5" },
              emphasis: { disabled: true },
              tooltip: { show: false },
              label: { show: false },
            },
          ],
        },
        {
          type: "pie",
          radius: [this.shareRadius(this.slotSpan(key))[1] * 0.72, this.shareRadius(this.slotSpan(key))[1] * 0.78],
          center: ["50%", "50%"],
          startAngle: 90,
          label: { show: false },
          data: [
            {
              value: Math.max(er, 0.01),
              name: item.term,
              itemStyle: { color, borderRadius: 8 },
            },
            {
              value: Math.max(maxEr - er, 0.01),
              name: "empty",
              itemStyle: { color: "transparent" },
              emphasis: { disabled: true },
              tooltip: { show: false },
            },
          ],
        },
      ];
    },

    applyFocus(key, item) {
      if (this.mentionView !== "chart") return;
      this.clearHot();
      const prev = this.focus && this.focus.key;
      if (prev && prev !== key) this.restoreChart(prev);
      this.focus = { key, item };
      this.tip = { key, html: this.tooltipHtml(key, item) };
      this.$nextTick(() => {
        const chart = this.chartRefs[key];
        if (!chart) return;
        chart.setOption({
          backgroundColor: "transparent",
          animation: false,
          legend: { show: false },
          tooltip: { show: false },
          series: this.focusSeries(key, item),
          graphic: [],
        }, { notMerge: true });
      });
    },

    restoreChart(key) {
      if (this.tip && this.tip.key === key) this.tip = null;
      this.paintResting(key);
    },

    clearFocus() {
      const key = this.focus && this.focus.key;
      this.focus = null;
      this.pinned = false;
      if (key) this.restoreChart(key);
    },

    /** Наведение на строку — подсветка сегмента доли и ER-кольца. */
    highlightSlice(key, dataIndex) {
      const chart = this.chartRefs[key];
      if (!chart || dataIndex < 0) return;
      chart.dispatchAction({ type: "highlight", seriesIndex: 0, dataIndex });
      chart.dispatchAction({ type: "highlight", seriesIndex: dataIndex + 1, dataIndex: 0 });
    },
    downplaySlice(key, dataIndex) {
      const chart = this.chartRefs[key];
      if (!chart) return;
      if (dataIndex == null || dataIndex < 0) {
        chart.dispatchAction({ type: "downplay", seriesIndex: 0 });
        return;
      }
      chart.dispatchAction({ type: "downplay", seriesIndex: 0, dataIndex });
      chart.dispatchAction({ type: "downplay", seriesIndex: dataIndex + 1, dataIndex: 0 });
    },

    disposeCharts() {
      this.clearHot();
      this.charts.forEach((c) => c.dispose());
      this.charts = [];
      this.chartRefs = {};
    },

    resizeCharts() {
      if (!this.hasData) return;
      this.$nextTick(this.renderCharts);
    },

    scheduleRender() {
      // не блокируем клик/таб: рисуем после кадра отрисовки
      if (this._renderFrame) cancelAnimationFrame(this._renderFrame);
      this._renderFrame = requestAnimationFrame(() => {
        this._renderFrame = null;
        this.renderCharts();
      });
    },

    load() {
      // silent: не прячем кольца на время перезапроса — dispose/init только
      // когда уже есть свежие данные (см. renderCharts)
      return this.cmLoad(
        () => cmApi.fetchMentions(this.cmScope, this.scope),
        (data) => {
          this.data = data;
          if (!this.hasData) this.disposeCharts();
        },
        { silent: true },
      ).then((fresh) => {
        if (fresh && this.hasData) this.$nextTick(this.scheduleRender);
      });
    },
  },

  created() {
    // см. Cm-timelines: пачку resize-событий схлопываем в одну перерисовку
    this.onResize = debounce(this.resizeCharts, 150);
  },

  mounted() {
    window.addEventListener("resize", this.onResize);
    document.addEventListener("pointerdown", this.onDocPointer);
  },

  beforeDestroy() {
    document.removeEventListener("pointerdown", this.onDocPointer);
    window.removeEventListener("resize", this.onResize);
    this.onResize.cancel();
    this.cancelFocus();
    this.cancelLeave();
    if (this._renderFrame) cancelAnimationFrame(this._renderFrame);
    this.disposeCharts();
  },
};
</script>
